import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type OcrReviewStatus = "PENDING" | "APPROVED" | "REJECTED" | "EDITED" | "FAILED";

export const OCR_STATUS_LABEL: Record<OcrReviewStatus, string> = {
  PENDING: "Pendiente de revisión",
  APPROVED: "Aprobada",
  REJECTED: "Rechazada",
  EDITED: "Editada",
  FAILED: "Falló el procesamiento",
};

export const OCR_STATUS_BADGE_CLASS: Record<OcrReviewStatus, string> = {
  PENDING: "bg-amber-100 text-amber-700",
  APPROVED: "bg-green-100 text-green-700",
  REJECTED: "bg-red-100 text-red-700",
  EDITED: "bg-blue-100 text-blue-700",
  FAILED: "bg-red-100 text-red-700",
};

export interface OcrExtractionRecord {
  id: string;
  documentId: string;
  status: OcrReviewStatus;
  rawText: string | null;
  extractedFields: Record<string, unknown>;
  confidence: string | null;
  errorMessage: string | null;
  reviewNotes: string | null;
  reviewedAt: string | null;
  createdAt: string;
  document?: { id: string; clientId: string; originalName: string; category: string; mimeType: string } | null;
  reviewedBy?: { id: string; fullName: string; email: string } | null;
}

export interface ListOcrExtractionsFilters {
  status?: OcrReviewStatus;
  page?: number;
  pageSize?: number;
}

export function useOcrExtractions(filters: ListOcrExtractionsFilters = {}) {
  return useQuery({
    queryKey: ["ocr-extractions", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<OcrExtractionRecord[]>>("/ocr-extractions", { params: filters });
      return data;
    },
  });
}

export function useOcrExtraction(id: string | null) {
  return useQuery({
    queryKey: ["ocr-extractions", id],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<OcrExtractionRecord>>(`/ocr-extractions/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useDocumentOcrExtraction(clientId: string | undefined, documentId: string | undefined) {
  return useQuery({
    queryKey: ["clients", clientId, "documents", documentId, "ocr"],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get<ApiSuccessResponse<OcrExtractionRecord>>(`/clients/${clientId}/documents/${documentId}/ocr`);
        return data.data;
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    enabled: !!clientId && !!documentId,
    // Mientras el worker de OCR todavía no procesó el documento (no existe
    // fila de extracción todavía), reintenta cada pocos segundos para que la
    // insignia se actualice sola sin que el usuario recargue la página.
    refetchInterval: (query) => (query.state.data === null ? 3000 : false),
  });
}

export interface ReviewOcrExtractionPayload {
  id: string;
  status: "APPROVED" | "REJECTED" | "EDITED";
  extractedFields?: Record<string, unknown>;
  reviewNotes?: string;
}

export function useReviewOcrExtraction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: ReviewOcrExtractionPayload) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<OcrExtractionRecord>>(`/ocr-extractions/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ocr-extractions"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

export function useRetryOcrExtraction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, documentId }: { clientId: string; documentId: string }) => {
      await apiClient.post(`/clients/${clientId}/documents/${documentId}/ocr/retry`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ocr-extractions"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}
